import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import {
  getDeviceRateHistory,
  type Device,
  type DeviceRateHistory,
  type DeviceSensorRateSeries,
} from '../api/devices';
import { extractErrorMessage } from '../api/errors';
import { timeAgo } from '../util/deviceHealth';
import { sortRows, useTableSort } from '../util/tableSort';
import { SortHeader } from './SortHeader';

const SENSOR_LABELS: Record<string, string> = {
  muse: 'Muse EEG',
  'metawear-left': 'Left Hand',
  'metawear-right': 'Right Hand',
};

/** Compact date + time for a sparkline x-axis tick — e.g. "7/15 2:30 PM".
 *  Returns '' for a missing or unparseable timestamp so a bad point just
 *  drops its label rather than rendering "Invalid Date". */
export function fmtAxisTick(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString(undefined, {
    month: 'numeric',
    day: 'numeric',
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date} ${time}`;
}

/** Rotated x-axis tick: the date/time reads bottom-to-top so the labels stack
 *  vertically instead of eating horizontal width (which crowded them out on a
 *  dense series). Recharts injects x/y/payload; `points` maps the index to its
 *  timestamp. Renders nothing for a bad/missing point. */
function VerticalDateTick(props: {
  x?: number;
  y?: number;
  payload?: { value: number };
  points?: { t: string }[];
  fill?: string;
}) {
  const { x, y, payload, points, fill } = props;
  if (x == null || y == null || !points) return null;
  const label = fmtAxisTick(points[payload?.value ?? -1]?.t);
  if (!label) return null;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={4}
        textAnchor="end"
        transform="rotate(-90)"
        fontSize={10}
        fontWeight={500}
        fill={fill ?? '#616161'}
      >
        {label}
      </text>
    </g>
  );
}

/** A sensor is a chronic under-performer if it was low/bad in ≥2 sessions
 *  AND at least a third of its recent sessions — a recurring problem, not a
 *  one-off bad take. */
function isChronic(s: DeviceSensorRateSeries): boolean {
  return s.low_count >= 2 && s.low_count / Math.max(1, s.total_count) >= 0.34;
}

/** Fraction of a sensor's graded sessions that hit target rate. */
function reliability(s: DeviceSensorRateSeries): number {
  return s.total_count ? (s.total_count - s.low_count) / s.total_count : 1;
}

/** Reliability → a plain-language grade. Derived purely from the low/total
 *  session counts we hold; this is NOT a signal-quality or battery metric
 *  (we don't have those). */
function sensorGrade(s: DeviceSensorRateSeries): {
  label: string;
  color: 'success' | 'default' | 'warning';
} {
  const r = reliability(s);
  if (r >= 0.95) return { label: 'Excellent', color: 'success' };
  if (r >= 0.8) return { label: 'Good', color: 'default' };
  return { label: 'Low', color: 'warning' };
}

/** One header stat — bold value over a small uppercase caption. */
function HeaderStat({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function SensorRow({ series }: { series: DeviceSensorRateSeries }) {
  const theme = useTheme();
  const label = SENSOR_LABELS[series.sensor_id] ?? series.sensor_id;
  const points = series.points.map((p, i) => ({
    i,
    hz: p.achieved_hz,
    t: p.started_at,
  }));
  const latest = series.points[series.points.length - 1];
  // Delta to target for the latest session — how far the achieved rate landed
  // from the target (0 = on target, negative = below). Replaces the ± jitter:
  // a delta answers "was this session at rate?" more directly than the spread.
  const latestDelta =
    latest?.achieved_hz != null && series.target_hz != null
      ? Math.round(latest.achieved_hz) - series.target_hz
      : null;
  const chronic = isChronic(series);
  // `absent` = the sensor wasn't connected for that recording (an EEG-only
  // case, say). It says nothing about the device's health, so it must not
  // read as a problem here — the API already keeps it out of `low_count`.
  const latestAbsent = latest?.status === 'absent';
  const latestLow =
    !latestAbsent &&
    (latest?.status === 'low' || latest?.status === 'bad');
  const grade = sensorGrade(series);

  return (
    <Box sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {/* Identity on the left as a compact stacked label — a FIXED width on
            every row so all the charts start at the same x and their vertical
            guide lines line up straight down. */}
        <Box sx={{ width: 140, flexShrink: 0, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Typography variant="subtitle2" noWrap>
              {label}
            </Typography>
            <Chip
              size="small"
              label={grade.label}
              color={grade.color}
              variant={grade.color === 'default' ? 'outlined' : 'filled'}
              sx={{
                height: 18,
                flexShrink: 0,
                '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' },
              }}
            />
          </Stack>
          {series.device_name && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: 'block' }}
            >
              {series.device_name}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{ display: 'block', fontFamily: 'monospace', fontWeight: 600 }}
            color={latestLow ? 'error.main' : 'text.secondary'}
          >
            {latest?.achieved_hz != null
              ? `${Math.round(latest.achieved_hz)} Hz`
              : '—'}
            {series.target_hz != null && ` / ${series.target_hz}`}
            {latestDelta != null && latestDelta !== 0 && (
              <Typography
                component="span"
                variant="caption"
                color={latestDelta < 0 ? 'warning.main' : 'text.secondary'}
              >
                {' '}
                Δ{latestDelta > 0 ? '+' : '−'}
                {Math.abs(latestDelta)}
              </Typography>
            )}
            {chronic
              ? ` · chronic ${series.low_count}/${series.total_count}`
              : series.low_count > 0
                ? ` · ${series.low_count}/${series.total_count} low`
                : ''}
          </Typography>
        </Box>
        {/* Chart on the right — starts where the label ends. Uniform compact
            height across all rows; the shared date axis lives in its own strip
            below (SharedTimeAxis). */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={56}>
          <LineChart data={points} margin={{ top: 4, bottom: 0, left: 0, right: 8 }}>
            <YAxis hide domain={[0, series.target_hz ?? 'auto']} />
            {/* Soft dotted verticals at every session, aligned across all the
                stacked sensor charts + the axis strip (same x-domain + margins)
                so you can trace one moment in time straight down. */}
            <CartesianGrid
              vertical
              horizontal={false}
              stroke={theme.palette.divider}
              strokeDasharray="2 4"
            />
            {/* Hidden axis: keeps the scale + grid ticks; the visible dates are
                drawn once by SharedTimeAxis below. */}
            <XAxis
              dataKey="i"
              tick={false}
              minTickGap={14}
              interval="preserveStartEnd"
              axisLine={false}
              tickLine={false}
              height={0}
            />
            {series.target_hz != null && (
              <ReferenceLine
                y={series.target_hz}
                stroke="#9e9e9e"
                strokeDasharray="3 3"
              />
            )}
            <Line
              type="monotone"
              dataKey="hz"
              stroke={chronic || latestLow ? '#d32f2f' : '#2e7d32'}
              strokeWidth={2}
              dot={{ r: 2 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
        </Box>
      </Stack>
    </Box>
  );
}

/** Shared date/time x-axis for the stacked sensor charts. Every sensor shares
 *  the same session times, so the rotated date/time labels — and the dotted
 *  guide lines — are drawn ONCE here, below the rows, offset by the same label
 *  width + margins so they line up with the chart column above. */
function SharedTimeAxis({ points }: { points: { i: number; t: string }[] }) {
  const theme = useTheme();
  if (points.length === 0) return null;
  return (
    // Pull the strip up so the dates sit right under the last chart instead of
    // floating a full row-gap below it.
    <Stack direction="row" spacing={1.5} sx={{ mt: -1.5 }}>
      <Box sx={{ width: 140, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={98}>
          <LineChart
            data={points}
            margin={{ top: 2, bottom: 0, left: 0, right: 8 }}
          >
            <YAxis hide />
            <CartesianGrid
              vertical
              horizontal={false}
              stroke={theme.palette.divider}
              strokeDasharray="2 4"
            />
            <XAxis
              dataKey="i"
              tick={
                <VerticalDateTick
                  points={points}
                  fill={theme.palette.text.primary}
                />
              }
              tickMargin={4}
              minTickGap={14}
              interval="preserveStartEnd"
              axisLine={false}
              tickLine={false}
              height={92}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Stack>
  );
}

/** Telemetry header: overall reliability across sensors, graded-session
 *  count, and when the device last recorded. Overall = % of graded sessions
 *  (summed over sensors) that hit target rate — a real aggregate, not a
 *  signal-quality reading. */
function HealthHeader({ history }: { history: DeviceRateHistory }) {
  const totals = history.sensors.reduce(
    (a, s) => ({ low: a.low + s.low_count, tot: a.tot + s.total_count }),
    { low: 0, tot: 0 },
  );
  const overall = totals.tot
    ? Math.round(((totals.tot - totals.low) / totals.tot) * 100)
    : null;
  let lastRec: string | null = null;
  for (const s of history.sensors) {
    for (const p of s.points) {
      if (p.started_at && (!lastRec || p.started_at > lastRec)) {
        lastRec = p.started_at;
      }
    }
  }
  return (
    <Stack
      direction="row"
      spacing={3}
      sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1.5 }}
    >
      {overall != null && <HeaderStat label="Overall" value={`${overall}%`} />}
      <HeaderStat label="Sessions" value={String(history.session_count)} />
      {lastRec && <HeaderStat label="Last recording" value={timeAgo(lastRec)} />}
    </Stack>
  );
}

/** One session's row for the per-session table: the session's time plus each
 *  sensor's achieved Hz for that session. */
export interface PerSessionRateRow {
  session_id: string;
  started_at: string;
  /** sensor_id → { hz, status } for the sensors that recorded this session. */
  bySensor: Record<string, { hz: number | null; status: string | null }>;
}

/** Pivot the per-sensor rate series into per-session rows (newest first) — one
 *  row per session, carrying each sensor's achieved rate that session. Pure +
 *  exported for testing. */
export function sessionRowsFromHistory(
  history: DeviceRateHistory,
): PerSessionRateRow[] {
  const bySession = new Map<string, PerSessionRateRow>();
  for (const s of history.sensors) {
    for (const p of s.points) {
      let row = bySession.get(p.session_id);
      if (!row) {
        row = { session_id: p.session_id, started_at: p.started_at, bySensor: {} };
        bySession.set(p.session_id, row);
      }
      if (p.started_at && (!row.started_at || p.started_at < row.started_at)) {
        row.started_at = p.started_at;
      }
      row.bySensor[s.sensor_id] = { hz: p.achieved_hz, status: p.status };
    }
  }
  return [...bySession.values()].sort((a, b) =>
    String(b.started_at).localeCompare(String(a.started_at)),
  );
}

/** The explicit per-session view: the actual achieved sampling rate each time
 *  this device recorded — newest first, one column per sensor. The sparklines
 *  above show the trend; this answers "what rate did each session actually
 *  get?" for every use. */
function PerSessionRatesTable({ history }: { history: DeviceRateHistory }) {
  const allRows = useMemo(() => sessionRowsFromHistory(history), [history]);
  // Columns are dynamic (one per sensor): 'started_at', or the sensor_id.
  // Default: newest session first, as before.
  const sort = useTableSort<string>('started_at', 'desc');
  const rows = useMemo(
    () =>
      sortRows(
        allRows,
        sort.orderBy === 'started_at'
          ? (r) => r.started_at
          : (r) => r.bySensor[sort.orderBy]?.hz ?? null,
        sort.order,
      ),
    [allRows, sort.orderBy, sort.order],
  );
  if (rows.length === 0) return null;
  const sensors = history.sensors;
  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography variant="subtitle2">Per-session rates</Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1 }}
      >
        The achieved sampling rate (Hz) each time this device recorded, newest
        first. A rate that fell below target is flagged red.
      </Typography>
      <TableContainer sx={{ maxHeight: 280 }}>
        <Table size="small" stickyHeader aria-label="per-session sampling rates">
          <TableHead>
            <TableRow>
              <SortHeader label="Session" col="started_at" sort={sort} />
              {sensors.map((s) => (
                <SortHeader
                  key={s.sensor_id}
                  col={s.sensor_id}
                  sort={sort}
                  align="right"
                  label={
                    <>
                      {SENSOR_LABELS[s.sensor_id] ?? s.sensor_id}
                      {s.target_hz != null && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {' '}
                          / {s.target_hz}
                        </Typography>
                      )}
                    </>
                  }
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.session_id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {fmtAxisTick(r.started_at) || '—'}
                </TableCell>
                {sensors.map((s) => {
                  const cell = r.bySensor[s.sensor_id];
                  const low =
                    cell?.status !== 'absent' &&
                    (cell?.status === 'low' || cell?.status === 'bad');
                  return (
                    <TableCell
                      key={s.sensor_id}
                      align="right"
                      sx={{
                        fontFamily: 'monospace',
                        color: low ? 'error.main' : 'text.primary',
                        fontWeight: low ? 700 : 400,
                      }}
                    >
                      {cell?.hz != null ? Math.round(cell.hz) : '—'}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

interface Props {
  open: boolean;
  tenantId: string | null;
  device: Device | null;
  onClose: () => void;
}

/**
 * Per-device sensor health (ROADMAP CAPTURE-DEVICE-RATE-HISTORY). Opens from a
 * Devices-page row and sparklines each sensor's achieved Hz across this
 * device's recent sessions, flagging one that chronically under-rates — the
 * fleet-maintenance view over the per-session capture-quality badge.
 */
export function SensorHealthDialog({ open, tenantId, device, onClose }: Props) {
  const [history, setHistory] = useState<DeviceRateHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !tenantId || !device) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setHistory(null);
    getDeviceRateHistory(tenantId, device.id)
      .then((h) => {
        if (!cancelled) setHistory(h);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(extractErrorMessage(e, 'failed to load sensor health'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tenantId, device]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Sensor health
        {device && (
          <Typography variant="body2" color="text.secondary">
            {device.label}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && history && (
          <>
            {history.sensors.length > 0 && <HealthHeader history={history} />}
            <Typography variant="caption" color="text.secondary">
              Achieved sampling rate per sensor across this device's last{' '}
              {history.session_count} graded session
              {history.session_count === 1 ? '' : 's'} (oldest → newest, with
              each session's date/time below; dashed line = target).
            </Typography>
            {history.sensors.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No capture-quality data yet for this device. Rates appear once it
                records a session on a build that reports quality.
              </Alert>
            ) : (
              <>
                {history.sensors.map((s) => (
                  <SensorRow key={s.sensor_id} series={s} />
                ))}
                <SharedTimeAxis
                  points={history.sensors
                    .reduce((a, b) => (b.points.length > a.points.length ? b : a))
                    .points.map((p, i) => ({ i, t: p.started_at }))}
                />
                <PerSessionRatesTable history={history} />
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
